import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, X, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: 'Safety Committee',
    scheduled_date: '',
    duration: 60,
    location: '',
    is_mandatory: false,
    agenda_items: [],
    attendees: [],
    key_decisions: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAddAgendaItem = () => {
    setFormData({
      ...formData,
      agenda_items: [
        ...formData.agenda_items,
        { id: Date.now().toString(), topic: '', duration: 10, presenter: '', notes: '' }
      ]
    });
  };

  const handleUpdateAgendaItem = (id, field, value) => {
    setFormData({
      ...formData,
      agenda_items: formData.agenda_items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  const handleRemoveAgendaItem = (id) => {
    setFormData({
      ...formData,
      agenda_items: formData.agenda_items.filter(item => item.id !== id)
    });
  };

  const handleAddAttendee = (user) => {
    if (formData.attendees.find(a => a.user_id === user.id)) return;
    
    setFormData({
      ...formData,
      attendees: [
        ...formData.attendees,
        {
          id: Date.now().toString(),
          user_id: user.id,
          user_name: user.name,
          role: user.role || 'Employee',
          attended: false
        }
      ]
    });
  };

  const handleRemoveAttendee = (id) => {
    setFormData({
      ...formData,
      attendees: formData.attendees.filter(a => a.id !== id)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        meeting_number: '',
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        status: 'Scheduled'
      };

      const response = await axios.post(`${API}/meetings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/meetings');
    } catch (err) {
      console.error('Submit error:', err);
      let errorMsg = 'Failed to create meeting';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/meetings')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Meetings
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Schedule Safety Meeting</h1>
          <p className="text-gray-600 mt-1">Plan a new safety meeting, toolbox talk, or committee session</p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardContent className="p-6 space-y-6">
              {/* Basic Details */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Meeting Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g., Monthly Safety Committee Meeting"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Meeting Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.meeting_type}
                      onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    >
                      <option value="Safety Committee">Safety Committee</option>
                      <option value="Toolbox Talk">Toolbox Talk</option>
                      <option value="Emergency Drill">Emergency Drill</option>
                      <option value="Management Review">Management Review</option>
                      <option value="Training Session">Training Session</option>
                      <option value="Incident Review">Incident Review</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Scheduled Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      min="5"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g., Conference Room A"
                      required
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="ml-2 text-sm font-semibold text-gray-700">
                      Mandatory Attendance
                    </label>
                  </div>
                </div>
              </div>

              {/* Agenda Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Agenda</h3>
                  <Button
                    type="button"
                    onClick={handleAddAgendaItem}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Topic
                  </Button>
                </div>

                {formData.agenda_items.length === 0 ? (
                  <p className="text-gray-500 text-sm">No agenda items yet. Click "Add Topic" to start.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.agenda_items.map((item, index) => (
                      <div key={item.id} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-500">Item #{index + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAgendaItem(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="md:col-span-2">
                            <input
                              type="text"
                              value={item.topic}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'topic', e.target.value)}
                              placeholder="Topic"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              required
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={item.duration}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'duration', parseInt(e.target.value))}
                              placeholder="Duration (min)"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              min="1"
                              required
                            />
                          </div>
                          <div className="md:col-span-3">
                            <input
                              type="text"
                              value={item.presenter}
                              onChange={(e) => handleUpdateAgendaItem(item.id, 'presenter', e.target.value)}
                              placeholder="Presenter"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attendees */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Attendees</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Add Attendees
                  </label>
                  <select
                    onChange={(e) => {
                      const user = users.find(u => u.id === e.target.value);
                      if (user) handleAddAttendee(user);
                      e.target.value = '';
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Select a user to add...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                    ))}
                  </select>
                </div>

                {formData.attendees.length === 0 ? (
                  <p className="text-gray-500 text-sm">No attendees added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.attendees.map(attendee => (
                      <div key={attendee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-900">{attendee.user_name}</p>
                          <p className="text-sm text-gray-600">{attendee.role}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttendee(attendee.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/meetings')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Schedule Meeting
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingsCreate;